import React from 'react'
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  InlineGrid,
  InlineStack,
  Link,
  Text,
  Badge,
  Icon,
} from '@shopify/polaris'
import { CheckCircleIcon } from '@shopify/polaris-icons'
import { useTranslation } from 'react-i18next'
import {
  calculateFlatCharge,
  formatBillingPeriod,
  formatDate,
  getRemainingTrialDays,
} from './accountHelpers'

/**
 * Account card component for FLAT/FIXED pricing strategy
 * Handles both trial and active subscription states with fixed pricing
 */
const FlatAccountCard = ({
  config,
  billingCycle,
  shopPlan,
  dashboardData,
  pricing,
  onOpenBilling,
}) => {
  const { t } = useTranslation()
  const isTrialPeriod = billingCycle?.isTrialPeriod || false
  
  // Calculate flat charge information
  const chargeInfo = calculateFlatCharge(shopPlan)
  const remainingTrialDays = getRemainingTrialDays(billingCycle)

  // ============================================
  // Trial Period Component
  // ============================================
  if (isTrialPeriod) {
    return (
      <Card padding={'0'}>
        {/* Trial Header */}
        <Box padding="400">
          <BlockStack gap="300">
            <Banner tone="info">
              <BlockStack gap="050">
                <Text variant="headingMd" as="h3">
                  {t('SettingsPage.Sections.Account.Card.Flat.trialTitle', { planName: config.planName })}
                </Text>
                <Text dangerouslySetInnerHTML={{ __html: t('SettingsPage.Sections.Account.Card.Flat.trialEndsOn', { date: formatDate(billingCycle.endingOn) }) }} />
                {remainingTrialDays > 0 && (
                  <Text variant="bodySm">
                    {t('SettingsPage.Sections.Account.Card.Flat.daysRemaining', { count: remainingTrialDays })}
                  </Text>
                )}
                <Text variant="bodySm" tone="subdued">
                  {t('SettingsPage.Sections.Account.Card.Flat.trialNote', { period: chargeInfo.isAnnual ? t('SettingsPage.Sections.Account.Card.annual') : t('SettingsPage.Sections.Account.Card.monthly') })}
                </Text>
              </BlockStack>
            </Banner>

            {/* Trial Plan Features */}
            <BlockStack gap="200">
              <Text variant="headingMd">{t('SettingsPage.Sections.Account.Card.Flat.fixedPricingPlan')}</Text>

              <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                <BlockStack gap="200">
                  <InlineGrid columns={'auto 1fr'} gap="200" blockAlign="start">
                    <Icon source={CheckCircleIcon} tone="success" />
                    <Text>{t('SettingsPage.Sections.Account.Card.Flat.features.unlimitedRecommendations')}</Text>
                  </InlineGrid>

                  <InlineGrid columns={'auto 1fr'} gap="200" blockAlign="start">
                    <Icon source={CheckCircleIcon} tone="success" />
                    <Text>{t('SettingsPage.Sections.Account.Card.Flat.features.allFeaturesIncluded')}</Text>
                  </InlineGrid>

                  <InlineGrid columns={'auto 1fr'} gap="200" blockAlign="start">
                    <Icon source={CheckCircleIcon} tone="success" />
                    <Text>{t('SettingsPage.Sections.Account.Card.Flat.features.noUsageLimits')}</Text>
                  </InlineGrid>

                  <InlineGrid columns={'auto 1fr'} gap="200" blockAlign="start">
                    <Icon source={CheckCircleIcon} tone="success" />
                    <Text>{t('SettingsPage.Sections.Account.Card.Flat.features.predictableBilling')}</Text>
                  </InlineGrid>
                </BlockStack>
              </Box>

              {/* Pricing after trial */}
              <Box paddingBlock="300" background="bg-surface" borderRadius="200">
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued">
                    {t('SettingsPage.Sections.Account.Card.Flat.afterTrialEnds')}
                  </Text>
                  <InlineStack align="space-between">
                    <Text variant="bodyMd" fontWeight="semibold">
                      {t('SettingsPage.Sections.Account.Card.Flat.chargeLabel', { period: chargeInfo.isAnnual ? t('SettingsPage.Sections.Account.Card.annual') : t('SettingsPage.Sections.Account.Card.monthly') })}
                    </Text>
                    <Text variant="bodyLg" fontWeight="semibold">
                      ${chargeInfo.isAnnual ? chargeInfo.annualCharge : chargeInfo.monthlyCharge}
                    </Text>
                  </InlineStack>
                </BlockStack>
              </Box>

              <Text variant="bodySm" tone="subdued">
                {t('SettingsPage.Sections.Account.Card.Flat.trialBenefit')}
              </Text>
            </BlockStack>

            {/* Upgrade CTA during trial */}
            <InlineStack align="end">
              <Button
                onClick={() => pricing.open({ abovePlans: dashboardData.shopPlan })}
              >
                {t('SettingsPage.Sections.Account.Card.viewPlans')}
              </Button>
            </InlineStack>
          </BlockStack>
        </Box>
      </Card>
    )
  }

  // ============================================
  // Active Subscription Component
  // ============================================
  return (
    <Card padding={'0'}>
      {/* Plan Header */}
      <Box padding="400">
        <BlockStack gap="300">
          <InlineGrid columns="1fr auto" gap="300" alignItems="center">
            <BlockStack gap="100">
              <InlineStack gap="200" blockAlign="center">
                <Text variant="headingMd" as="h3">
                  {t('SettingsPage.Sections.Account.Card.planLabel', { planName: config.planName })}
                </Text>
                <Badge tone="success">{t('SettingsPage.Sections.Account.Card.active')}</Badge>
              </InlineStack>
              <Text tone="subdued">
                {t('SettingsPage.Sections.Account.Card.Flat.pricingType', { period: chargeInfo.isAnnual ? t('SettingsPage.Sections.Account.Card.annual') : t('SettingsPage.Sections.Account.Card.monthly') })}
              </Text>
            </BlockStack>
            
            {config.upgradeRequired ? (
              <Button
                url="/pricing"
                primary
              >
                {t('SettingsPage.Sections.Account.upgradeBtnCta')}
              </Button>
            ) : (
              <Link monochrome url="/pricing">
                {t('DefaultText.downgradeText')}
              </Link>
            )}
          </InlineGrid>
        </BlockStack>
      </Box>

      <Divider />

      {/* Billing Information */}
      <Box padding="400">
        <BlockStack gap="300">
          <Text variant="headingMd">{t('SettingsPage.Sections.Account.Card.billingDetails')}</Text>

          <InlineGrid columns="1fr 1fr 1fr" gap="300">
            {/* Charge Amount */}
            <BlockStack gap="100">
              <Text tone="subdued">
                {chargeInfo.isAnnual ? t('SettingsPage.Sections.Account.Card.annualCharge') : t('SettingsPage.Sections.Account.Card.monthlyCharge')}
              </Text>
              <Text variant="bodyLg" fontWeight="semibold">
                ${chargeInfo.isAnnual ? chargeInfo.annualCharge : chargeInfo.monthlyCharge}
              </Text>
            </BlockStack>

            {/* Billing Period */}
            <BlockStack gap="100">
              <Text tone="subdued">{t('SettingsPage.Sections.Account.Card.currentPeriod')}</Text>
              <Text variant="bodyLg" fontWeight="semibold">
                {formatBillingPeriod(billingCycle)}
              </Text>
            </BlockStack>

            {/* Plan Type */}
            <BlockStack gap="100">
              <Text tone="subdued">{t('SettingsPage.Sections.Account.Card.planType')}</Text>
              <Box>
                <Badge tone="info">
                  {t('SettingsPage.Sections.Account.Card.Flat.planTypeBadge', { period: chargeInfo.isAnnual ? t('SettingsPage.Sections.Account.Card.annual') : t('SettingsPage.Sections.Account.Card.monthly') })}
                </Badge>
              </Box>
            </BlockStack>
          </InlineGrid>

          {/* Annual savings message */}
          {!chargeInfo.isAnnual && chargeInfo.annualCharge > 0 && (
            <Banner tone="info">
              <InlineStack gap="200">
                <Text variant="bodySm">
                  {t('SettingsPage.Sections.Account.Card.Flat.annualSavings', { percent: Math.round((1 - (chargeInfo.annualCharge / (chargeInfo.monthlyCharge * 12))) * 100) })}
                </Text>
                <Button plain onClick={() => pricing.open({ abovePlans: dashboardData.shopPlan })}>
                  {t('SettingsPage.Sections.Account.Card.learnMore')}
                </Button>
              </InlineStack>
            </Banner>
          )}
        </BlockStack>
      </Box>

      <Divider />

      {/* Plan Features */}
      <Box padding="400">
        <BlockStack gap="300">
          <Text variant="headingMd">{t('SettingsPage.Sections.Account.Card.planIncludes')}</Text>

          <InlineGrid columns="1fr 1fr" gap="300">
            <BlockStack gap="200">
              <InlineGrid columns="auto 1fr" gap="200" blockAlign="center">
                <Icon source={CheckCircleIcon} tone="success" />
                <Text>{t('SettingsPage.Sections.Account.Card.Flat.features.unlimitedRecommendations')}</Text>
              </InlineGrid>

              <InlineGrid columns="auto 1fr" gap="200" blockAlign="center">
                <Icon source={CheckCircleIcon} tone="success" />
                <Text>{t('SettingsPage.Sections.Account.Card.Flat.features.allWidgetTypes')}</Text>
              </InlineGrid>

              <InlineGrid columns="auto 1fr" gap="200" blockAlign="center">
                <Icon source={CheckCircleIcon} tone="success" />
                <Text>{t('SettingsPage.Sections.Account.Card.Flat.features.advancedAnalytics')}</Text>
              </InlineGrid>

              <InlineGrid columns="auto 1fr" gap="200" blockAlign="center">
                <Icon source={CheckCircleIcon} tone="success" />
                <Text>{t('SettingsPage.Sections.Account.Card.Flat.features.prioritySupport')}</Text>
              </InlineGrid>
            </BlockStack>

            <BlockStack gap="200">
              <InlineGrid columns="auto 1fr" gap="200" blockAlign="center">
                <Icon source={CheckCircleIcon} tone="success" />
                <Text>{t('SettingsPage.Sections.Account.Card.Flat.features.customBranding')}</Text>
              </InlineGrid>

              <InlineGrid columns="auto 1fr" gap="200" blockAlign="center">
                <Icon source={CheckCircleIcon} tone="success" />
                <Text>{t('SettingsPage.Sections.Account.Card.Flat.features.apiAccess')}</Text>
              </InlineGrid>

              <InlineGrid columns="auto 1fr" gap="200" blockAlign="center">
                <Icon source={CheckCircleIcon} tone="success" />
                <Text>{t('SettingsPage.Sections.Account.Card.Flat.features.bulkOperations')}</Text>
              </InlineGrid>

              <InlineGrid columns="auto 1fr" gap="200" blockAlign="center">
                <Icon source={CheckCircleIcon} tone="success" />
                <Text>{t('SettingsPage.Sections.Account.Card.Flat.features.noUsageLimits')}</Text>
              </InlineGrid>
            </BlockStack>
          </InlineGrid>

          <Box padding="300" background="bg-surface-secondary" borderRadius="200">
            <BlockStack gap="100">
              <Text variant="bodyMd" fontWeight="semibold">
                {t('SettingsPage.Sections.Account.Card.Flat.noRestrictions')}
              </Text>
              <Text variant="bodySm">
                {t('SettingsPage.Sections.Account.Card.Flat.noRestrictionsNote')}
              </Text>
            </BlockStack>
          </Box>
        </BlockStack>
      </Box>

      {/* Status Summary */}
      <Divider />
      <Box padding="400">
        <BlockStack gap="300">
          <Text variant="headingMd">{t('SettingsPage.Sections.Account.Card.accountStatus')}</Text>

          <InlineGrid columns="1fr 1fr" gap="300">
            <BlockStack gap="100">
              <InlineStack align="space-between">
                <Text>{t('SettingsPage.Sections.Account.Card.status')}</Text>
                <Badge tone="success">{t('SettingsPage.Sections.Account.Card.Flat.status.active')}</Badge>
              </InlineStack>

              <InlineStack align="space-between">
                <Text>{t('SettingsPage.Sections.Account.Card.Flat.status.nextBillingDate')}</Text>
                <Text fontWeight="semibold">
                  {formatDate(billingCycle?.endingOn)}
                </Text>
              </InlineStack>
            </BlockStack>

            <BlockStack gap="100">
              <InlineStack align="space-between">
                <Text>{t('SettingsPage.Sections.Account.Card.Flat.status.customerSince')}</Text>
                <Text fontWeight="semibold">
                  {formatDate(shopPlan?.createdAt)}
                </Text>
              </InlineStack>

              <InlineStack align="space-between">
                <Text>{t('SettingsPage.Sections.Account.Card.Flat.status.paymentMethod')}</Text>
                <Text fontWeight="semibold">
                  {t('SettingsPage.Sections.Account.Card.Flat.status.shopifyBilling')}
                </Text>
              </InlineStack>
            </BlockStack>
          </InlineGrid>
        </BlockStack>
      </Box>

      {/* Billing Management */}
      <Divider />
      <Box padding="400">
        <InlineGrid columns="1fr auto" gap="300" alignItems="center">
          <Text>
            {t('SettingsPage.Sections.Account.Card.Billing.description')}
          </Text>
          <Button onClick={onOpenBilling}>
            {t('SettingsPage.Sections.Account.Card.Billing.btnCta')}
          </Button>
        </InlineGrid>
      </Box>
    </Card>
  )
}

export default FlatAccountCard